<template>
  <div class="kanban-column" :class="{ 'is-first-column': isFirstColumn }">
    <div
      class="kanban-card-list"
      :class="{ forbidden: isForbiddenColumn }"
      @dragenter.prevent="onColumnDragOver"
      @dragover.prevent="onColumnDragOver"
      @dragleave="onCardListDragLeave"
      @drop.prevent="onCardListDrop"
    >
      <KanbanCard
        v-for="(task, taskIndex) in columnTasks"
        :key="`${uniqueColumnKey}-${task.id}`"
        :drop-target-id="`${uniqueColumnKey}-${task.id}`"
        :task="task"
        :taskIndex="taskIndex"
        :keysToRender="keysToRender"
        :dragContext="dragContext"
        :database="database"
        :actionFlowList="actionFlowList"
        :isDatabaseDropDownLoading="isDatabaseDropDownLoading"
        :actionDropdownApiLoading="actionDropdownApiLoading"
        :isItemSelected="isItemSelected"
        :statusFieldKey="statusFieldKey"
        :columnKey="headerItem.value"
        :isNoStatusColumn="isNoStatusColumn"
        :statusFieldRequired="statusFieldRequired"
        @onActionTrigger="(e) => $emit('onActionTrigger', e)"
        @onDragStart="(e) => $emit('onDragStart', e)"
        @onDrop="(e) => $emit('onDrop', e)"
        @onForbiddenDrop="(message) => $emit('onForbiddenDrop', message)"
        @onDragEnd="$emit('onDragEnd')"
      />

      <div
        v-if="canAddItem && !isForbiddenColumn"
        class="add-task-wrapper"
        :class="{ 'is-drop-target': showColumnEndDropLine }"
        @click="!isForbiddenColumn && $emit('onAddNewItem')"
        @dragenter.prevent="onAddTaskDragOver"
        @dragover.prevent="onAddTaskDragOver"
        @drop.stop.prevent="onDrop"
      >
        <div class="line" v-show="showColumnEndDropLine"></div>
        <img src="../../assets/img/muted-plus-icon.svg" />
      </div>

      <div
        v-if="!canAddItem && showColumnEndDropLine"
        class="column-end-drop-line"
      ></div>

      <div v-if="isForbiddenDropTarget" class="drop-forbidden-overlay">
        <!-- <i class="fas fa-ban"></i> -->
        <span>Status ist ein Pflichtfeld</span>
      </div>
    </div>
  </div>
</template>

<script>
import KanbanCard from './KanbanCard.vue';

export default {
  name: 'kanbanColumn',

  props: {
    uniqueColumnKey: { type: String, required: true },
    columnTasks: { type: Array, default: () => [] },
    keysToRender: { type: Array, default: () => [] },
    dragContext: { type: Object, required: true },
    groupKey: { type: String, required: true },
    headerItem: { type: Object, required: true },
    isFirstColumn: { type: Boolean, default: false },
    database: { type: Object, required: true },
    actionFlowList: { type: Array, default: () => [] },
    isDatabaseDropDownLoading: { type: Function, default: () => {} },
    actionDropdownApiLoading: { type: Boolean, default: false },
    isItemSelected: { type: Function, default: () => {} },
    statusFieldKey: { type: String, default: 'status' },
    canAddItem: { type: Boolean, default: false },
    statusFieldRequired: { type: Boolean, default: false },
    isNoStatusColumn: { type: Boolean, default: false },
  },

  data() {
    return {};
  },

  methods: {
    clearActiveDropTarget() {
      this.$set(this.dragContext, 'activeDropTargetId', null);
      this.$set(this.dragContext, 'activeDropPosition', null);
      this.$set(this.dragContext, 'activeDropForbiddenId', null);
      this.$set(this.dragContext, 'activeDropForbiddenMessage', null);
    },

    setForbiddenDropTarget() {
      this.$set(
        this.dragContext,
        'activeDropForbiddenId',
        this.headerItem.value
      );
      this.$set(
        this.dragContext,
        'activeDropForbiddenMessage',
        'Status ist ein Pflichtfeld'
      );
      this.$set(this.dragContext, 'activeDropTargetId', null);
      this.$set(this.dragContext, 'activeDropPosition', null);
    },

    isEventOverCard(event) {
      const cardElement = event.target?.closest?.('.kanban-card-wrapper');
      return Boolean(cardElement && event.currentTarget.contains(cardElement));
    },

    setColumnEndDropTarget() {
      this.$set(
        this.dragContext,
        'activeDropTargetId',
        this.lastCardDropTargetId || this.columnEndDropTargetId
      );
      this.$set(this.dragContext, 'activeDropPosition', 'after');
      this.$set(this.dragContext, 'activeDropForbiddenId', null);
      this.$set(this.dragContext, 'activeDropForbiddenMessage', null);
    },

    onCardListDragLeave(event) {
      if (!this.dragContext.isDragging) return;

      const listElement = event.currentTarget;
      const clientX = event.clientX;
      const clientY = event.clientY;

      window.requestAnimationFrame(() => {
        const elementUnderPointer = document.elementFromPoint(clientX, clientY);

        if (
          !elementUnderPointer ||
          !listElement.contains(elementUnderPointer)
        ) {
          this.clearActiveDropTarget();
        }
      });
    },

    onColumnDragOver(event) {
      if (!this.dragContext.isDragging) return;

      if (this.isForbiddenColumn) {
        this.setForbiddenDropTarget();
        return;
      }

      if (this.isEventOverCard(event)) {
        return;
      }

      this.setColumnEndDropTarget();
    },

    onAddTaskDragOver() {
      if (!this.dragContext.isDragging) return;

      if (this.isForbiddenColumn) {
        this.setForbiddenDropTarget();
        return;
      }

      this.setColumnEndDropTarget();
    },

    onCardListDrop(event) {
      if (this.isEventOverCard(event)) return;

      this.onDrop();
    },

    onDrop() {
      if (this.isForbiddenColumn) {
        this.clearActiveDropTarget();
        this.$emit('onForbiddenDrop', 'Status ist ein Pflichtfeld');
        return;
      }

      const dropTaskIndex = this.isEmptyColumn
        ? 0
        : this.columnTasks.length - 1;
      const dropPosition = this.isEmptyColumn ? 'before' : 'after';

      this.$emit('onDrop', {
        dropTaskIndex,
        dropPosition,
        task: { [this.groupKey]: this.headerItem.value },
      });

      this.clearActiveDropTarget();
    },
  },

  computed: {
    columnEndDropTargetId() {
      return `${this.uniqueColumnKey}-column-end`;
    },
    lastCardDropTargetId() {
      const lastTask = this.columnTasks[this.columnTasks.length - 1];
      if (!lastTask) return null;

      return `${this.uniqueColumnKey}-${lastTask.id}`;
    },
    showColumnEndDropLine() {
      return (
        this.isEmptyColumn &&
        this.dragContext?.activeDropTargetId === this.columnEndDropTargetId
      );
    },
    isEmptyColumn() {
      return this.columnTasks.length === 0;
    },
    isForbiddenColumn() {
      return this.isNoStatusColumn && this.statusFieldRequired;
    },
    isForbiddenDropTarget() {
      return this.dragContext?.activeDropForbiddenId === this.headerItem.value;
    },
  },

  components: {
    KanbanCard,
  },
};
</script>

<style scoped>
.kanban-column {
  width: 100%;
  min-width: 315px;
  max-width: 315px;
  position: relative;
  display: flex;
  flex-direction: column;
  height: auto;
  padding-bottom: 8px;
  align-self: stretch;
}

.kanban-column::-webkit-scrollbar {
  display: none;
}

.kanban-column .add-task-wrapper {
  height: 52px;
  min-height: 52px;
  padding: 0;
  text-align: center;
  box-shadow: 0px 0px 5px 0px #00000026;
  border-radius: 10px;
  cursor: pointer;
  position: relative;
  background: #fff;
  transition: background-color 200ms ease-in-out;
  margin-top: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
}

.kanban-column .add-task-wrapper img,
.kanban-column .add-task-wrapper .line {
  pointer-events: none;
}

.kanban-column .add-task-wrapper:hover {
  background: #f2efef;
}

.kanban-column .add-task-wrapper.is-drop-target,
.kanban-column .add-task-wrapper.is-drop-target:hover {
  background: #fff;
}

.kanban-column .add-task-wrapper.forbidden {
  cursor: not-allowed;
  background: #fff5f5;
  border: 1px dashed #ff4d4f;
}

.kanban-column .add-task-wrapper.forbidden:hover {
  background: #fff5f5;
}

.kanban-column .kanban-card-list {
  position: relative;
  display: flex;
  flex-direction: column;
  flex: 1;
  height: auto;
  min-height: 100%;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
}

.kanban-column .kanban-card-list.forbidden {
  min-height: 92px;
}

.kanban-column.is-first-column .kanban-card-list {
  padding-left: 8px;
}

.kanban-column .add-task-wrapper .line:before {
  content: '';
  width: 100%;
  height: 4px;
  position: absolute;
  left: 0;
  background: #009cff;
  top: -6px;
}

.kanban-column .column-end-drop-line {
  position: relative;
  height: 1px;
}

.kanban-column .column-end-drop-line:before {
  content: '';
  width: 100%;
  height: 4px;
  position: absolute;
  left: 0;
  background: #009cff;
  top: -6px;
}

.kanban-column .add-task-wrapper.forbidden .forbidden-hint,
.kanban-column .drop-forbidden-overlay {
  display: flex;
  padding-top: 20px;
  justify-content: center;
  gap: 8px;
  color: #d93025;
  font-family: 'AktivGroteskMedium';
  font-size: 13px;
}

.kanban-column .drop-forbidden-overlay {
  position: absolute;
  inset: 0;
  background: rgba(255, 245, 245, 0.82);
  border: 1px dashed #ff4d4f;
  border-radius: 10px;
  pointer-events: none;
  z-index: 5;
}
</style>
